import { createContext, useContext, useState, useCallback } from "react";
import type { Booking } from "@/types/Booking";
import type { Room } from "@/types/Room";
import type { ReactNode } from "react";

// Screen identifiers for history-based navigation
export type ScreenId =
  | "change-what"
  | "datetime"
  | "duration"
  | "room"
  | "duration-confirmation"
  | "room-confirmation"
  | "confirmation"
  | "slot-booking-restriction";

export type ChangeBookingFlowState = {
  // Legacy step management (kept for backwards compatibility during transition)
  step: number;
  setStep: (step: number) => void;

  // History-based navigation
  screenHistory: ScreenId[];
  currentScreen: ScreenId | null;
  navigateTo: (screen: ScreenId) => void;
  goBack: () => void;
  canGoBack: boolean;

  booking: Booking | null;
  setBooking: (booking: Booking | null) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  changingWhat: "datetime" | "duration" | "room" | null;
  setChangingWhat: (what: "datetime" | "duration" | "room" | null) => void;
  error: string | null;
  setError: (error: string | null) => void;
  newStartTime: string | null;
  setNewStartTime: (startTime: string | null) => void;
  newEndTime: string | null;
  setNewEndTime: (endTime: string | null) => void;
  newRoom: Room | null;
  setNewRoom: (room: Room | null) => void;
  resetFlow: () => void;
};

const ChangeBookingFlowContext = createContext<ChangeBookingFlowState | undefined>(undefined);

export const useChangeBookingFlow = () => {
  const context = useContext(ChangeBookingFlowContext);
  if (!context) {
    throw new Error("useChangeBookingFlow must be used within a ChangeBookingFlowProvider");
  }
  return context;
};

type ChangeBookingFlowProviderProps = {
  children: ReactNode;
  booking: Booking | null;
  initialChangeType?: "datetime" | "duration" | "room" | null;
};

export const ChangeBookingFlowProvider = ({ children, booking, initialChangeType = "datetime" }: ChangeBookingFlowProviderProps) => {
  const [step, setStep] = useState(1);
  const [currentBooking, setBooking] = useState<Booking | null>(booking);
  const [isLoading, setIsLoading] = useState(false);
  const [changingWhat, setChangingWhat] = useState<"datetime" | "duration" | "room" | null>(initialChangeType);
  const [error, setError] = useState<string | null>(null);
  const [newStartTime, setNewStartTime] = useState<string | null>(null);
  const [newEndTime, setNewEndTime] = useState<string | null>(null);
  const [newRoom, setNewRoom] = useState<Room | null>(null);

  // History-based navigation state
  const [screenHistory, setScreenHistory] = useState<ScreenId[]>([]);

  // Current screen is the last item in history, or null if history is empty
  const currentScreen = screenHistory.length > 0 ? screenHistory[screenHistory.length - 1] : null;
  const canGoBack = screenHistory.length > 1;

  // Navigate to a new screen (adds to history)
  const navigateTo = useCallback((screen: ScreenId) => {
    setScreenHistory((prev) => [...prev, screen]);
  }, []);

  // Go back to previous screen (removes current from history)
  const goBack = useCallback(() => {
    setScreenHistory((prev) => {
      if (prev.length <= 1) return prev;
      return prev.slice(0, -1);
    });
  }, []);

  const resetFlow = () => {
    setStep(1);
    setIsLoading(false);
    setChangingWhat(null);
    setError(null);
    setNewStartTime(null);
    setNewEndTime(null);
    setNewRoom(null);
    // Reset navigation history
    setScreenHistory([]);
  };

  const value: ChangeBookingFlowState = {
    // Legacy step management
    step,
    setStep,
    // History-based navigation
    screenHistory,
    currentScreen,
    navigateTo,
    goBack,
    canGoBack,
    // Booking data
    booking: currentBooking,
    setBooking,
    isLoading,
    setIsLoading,
    changingWhat,
    setChangingWhat,
    error,
    setError,
    newStartTime,
    setNewStartTime,
    newEndTime,
    setNewEndTime,
    newRoom,
    setNewRoom,
    resetFlow,
  };

  return <ChangeBookingFlowContext.Provider value={value}>{children}</ChangeBookingFlowContext.Provider>;
};
